import { beginLoading, endLoading, withLoading } from "./loadingSpinner";
import { clearMessages, createErrorMessages, createSuccessMessages, messagesReceived } from "./message";

import { TYPE_KEY } from "../common/constant";
import navigate from "../common/navigate";
import rest from "../common/rest";
import ObjectUtils from "../utils/ObjectUtils";
import { LOADED } from "./respondentsOverview";
import { hasSurveyDuplicatePageKeys, hasSurveyDuplicateQuestionKeys } from "./surveyEditor";

export const PERIOD_START = "periodStart";
export const PERIOD_END = "periodEnd";
export const CAN_BE_ACTIVATED = "canBeActivated";
export const NUMBER_OF_POSSIBLE_PROJECTS = "numberOfPossibleProjects";
export const STEP = "step";
export const CURRENT_YEAR = "currentYear";
export const ACTIVATION_ACTIVE = "activationActive";

const SET_PERIOD = "TEMPLATE_ACTIVATION.SET_PERIOD";
const SWITCH_PAGE = "TEMPLATE.ACTIVATION.SWITCH_PAGE";
const VALIDATE_INFORMATION = "TEMPLATE_ACTIVATION.VALIDATION";
const SET_NUMBER_OF_POSSIBLE_PROJECTS = "TEMPLATE_ACTIVATION.SET_NUMBER_OF_POSSIBLE_PROJECTS";
const RESET_STORE = "TEMPLATE.ACTIVATION.RESET_STORE";
const SWITCH_ACTIVATION_ACTIVE_FLAG = "TEMPLATE.ACTIVATION.ACTIVE.FLAG";

const { getDeepProperty } = ObjectUtils;

let currentYear = new Date().getFullYear();
let begin = new Date(Date.UTC(currentYear, 0, 1, 0, 0, 0, 0)).getTime();
let end = new Date(Date.UTC(currentYear, 11, 31, 23, 59, 59, 999)).getTime();
const initialState = {
  [PERIOD_START]: begin,
  [PERIOD_END]: end,
  [CAN_BE_ACTIVATED]: false,
  [NUMBER_OF_POSSIBLE_PROJECTS]: 0,
  [STEP]: true,
  [CURRENT_YEAR]: currentYear.toString(),
  [ACTIVATION_ACTIVE]: false
};

export default function reducer(state = initialState, action) {
  let newState;

  switch (action[TYPE_KEY]) {
    case SET_PERIOD:
      newState = Object.assign({}, state, {
        [PERIOD_START]: action[PERIOD_START],
        [PERIOD_END]: action[PERIOD_END],
        [CURRENT_YEAR]: action[CURRENT_YEAR]
      });
      break;

    case VALIDATE_INFORMATION:
      newState = Object.assign({}, state, {
        [CAN_BE_ACTIVATED]: _isValid(state)
      });
      break;

    case SET_NUMBER_OF_POSSIBLE_PROJECTS:
      newState = Object.assign({}, state, {
        [NUMBER_OF_POSSIBLE_PROJECTS]: action[NUMBER_OF_POSSIBLE_PROJECTS],
        [LOADED]: false
      });
      break;

    case SWITCH_PAGE:
      newState = Object.assign({}, state, {
        [STEP]: !state[STEP]
      });
      break;

    case RESET_STORE:
      newState = Object.assign({}, initialState);
      break;

    case SWITCH_ACTIVATION_ACTIVE_FLAG:
      newState = Object.assign({}, state, {
        [ACTIVATION_ACTIVE]: action[ACTIVATION_ACTIVE]
      });
      break;

    default:
      newState = Object.assign({}, state, {});
      break;
  }

  return newState;
}

export const setPeriod = (periodStart, periodEnd, year) => {
  return {
    [TYPE_KEY]: SET_PERIOD,
    [PERIOD_START]: periodStart,
    [PERIOD_END]: periodEnd,
    [CURRENT_YEAR]: year
  };
};

export const validateActivation = (template) => {
  return async (dispatch, getState) => {
    const templateForActivation = await loadTemplateForDuplicateCheck(template, dispatch);
    if (!templateForActivation) {
      return;
    }

    if (hasTemplateDuplicateKeys(templateForActivation)) {
      dispatch(messagesReceived(createErrorMessages("template.activation.duplicate_key")));
      return;
    }

    dispatch({
      [TYPE_KEY]: VALIDATE_INFORMATION
    });

    let payload = {
      programId: getDeepProperty(getState(), "fundingArea", "selectedProgram", "templateActivation", "id"),
      programInstanceId: getDeepProperty(
        getState(),
        "fundingArea",
        "selectedProgramInstance",
        "templateActivation",
        "id"
      ),
      programInstanceForeignId: getDeepProperty(
        getState(),
        "fundingArea",
        "selectedProgramInstance",
        "templateActivation",
        "foreignId"
      ),
      programInstanceCategory: getDeepProperty(
        getState(),
        "fundingArea",
        "selectedProgramInstance",
        "templateActivation",
        "category"
      ),
      periodStart: getDeepProperty(getState(), "templateActivation", PERIOD_START),
      periodEnd: getDeepProperty(getState(), "templateActivation", PERIOD_END)
    };

    if (payload.programInstanceForeignId && payload.programInstanceCategory === "SAP") {
      let url = `/survey/synchronization/survey/projectData/?programInstanceForeignId=${payload.programInstanceForeignId}`;
      dispatch(beginLoading());
      dispatch(clearMessages());
      rest.doPost(url).then(
        (result) => {
          if (result) {
            newFunction(payload, templateForActivation, dispatch);
            dispatch(endLoading());
          } else {
            dispatch(messagesReceived(createErrorMessages("template.activation.invalid_request")));
            dispatch(endLoading());
          }
        },
        () => {
          dispatch(messagesReceived(createErrorMessages("template.activation.invalid_request")));
          dispatch(endLoading());
        }
      );
    } else if (payload.programId) {
      dispatch(clearMessages());
      newFunction(payload, templateForActivation, dispatch);
    } else {
      dispatch(messagesReceived(createErrorMessages("template.activation.program_must_be_choosen")));
    }
  };
};

export const activation = (template, close, isSemesterTypeProgram) => {
  return async (dispatch, getState) => {
    const templateForActivation = await loadTemplateForDuplicateCheck(template, dispatch);
    if (!templateForActivation) {
      return;
    }

    if (hasTemplateDuplicateKeys(templateForActivation)) {
      dispatch(messagesReceived(createErrorMessages("template.activation.duplicate_key")));
      return;
    }

    dispatch({
      [TYPE_KEY]: SWITCH_ACTIVATION_ACTIVE_FLAG,
      [ACTIVATION_ACTIVE]: true
    });

    let payload = {
      programId: getDeepProperty(getState(), "fundingArea", "selectedProgram", "templateActivation", "id"),
      programInstanceId: getDeepProperty(
        getState(),
        "fundingArea",
        "selectedProgramInstance",
        "templateActivation",
        "id"
      ),
      periodStart: getDeepProperty(getState(), "templateActivation", PERIOD_START),
      periodEnd: getDeepProperty(getState(), "templateActivation", PERIOD_END)
    };

    const url = isSemesterTypeProgram
        ? `/admin/publish/survey/preActivate/${templateForActivation.identifier}/version/${templateForActivation.version}`
        : `/admin/publish/survey/${templateForActivation.identifier}/version/${templateForActivation.version}`;

    return withLoading(
      dispatch,
      () => rest.doPost(url, JSON.stringify(payload)),
      (err) => {
        const errorKey = isSemesterTypeProgram
          ? "template.activation.template_not_in_lectorate"
          : "template.activation.invalid_request";
        dispatch(messagesReceived(createErrorMessages(errorKey)));
      }
    ).then((result) => {
      if (!result) return;

      if (result.numberOfPublished > 0 || (isSemesterTypeProgram && result.isPublished)) {
        dispatch(clearMessages());

        dispatch({
          [TYPE_KEY]: SET_NUMBER_OF_POSSIBLE_PROJECTS,
          [NUMBER_OF_POSSIBLE_PROJECTS]: result.numberOfPublished
        });

        dispatch({ [TYPE_KEY]: RESET_STORE });
        close();

        if (!isSemesterTypeProgram) {
          navigate.to("/userArea/respondentsOverview");
          setTimeout(
            () => dispatch(messagesReceived(createSuccessMessages("template.activation.succeeded"))),
            2000
          );
        } else {
          dispatch(messagesReceived(createSuccessMessages("template.activation.pre_activation_succeeded")));
        }
      } else {
        dispatch(messagesReceived(createErrorMessages("template.activation.invalid_data")));
      }

      // endLoading() already handled by the helper
      dispatch({
        [TYPE_KEY]: SWITCH_ACTIVATION_ACTIVE_FLAG,
        [ACTIVATION_ACTIVE]: false
      });
    });
  };


};

const _isValid = (state) => {
  return state[PERIOD_START] !== undefined && state[PERIOD_END] !== undefined;
};

function hasTemplateDuplicateKeys(template) {
  return (
    hasSurveyDuplicateQuestionKeys(template?.surveyTemplate) ||
    hasSurveyDuplicatePageKeys(template?.surveyTemplate)
  );
}

async function loadTemplateForDuplicateCheck(template, dispatch) {
  if (template?.surveyTemplate) {
    return template;
  }

  const identifier = template?.identifier ?? template?.surveyId;
  if (!identifier || !template?.version) {
    return template;
  }

  dispatch(beginLoading());
  try {
    const loadedTemplate = await rest.doGet(`/admin/admin/surveys/${identifier}/version/${template.version}`);
    return loadedTemplate?.surveyTemplate ? loadedTemplate : template;
  } catch (error) {
    dispatch(messagesReceived(createErrorMessages("template.activation.invalid_request")));
    return null;
  } finally {
    dispatch(endLoading());
  }
}

export const resetDialog = (close) => {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: RESET_STORE
    });

    close();
  };
};

function newFunction(payload, template, dispatch) {
  let url = `/admin/publish/survey/valid/${template.identifier}/?program=${payload.programId}`;

  if (payload.programInstanceId) {
    url = url + "&programInstance=" + payload.programInstanceId;
  }
  if (payload.periodStart) {
    url = url + "&start=" + payload.periodStart;
  }
  if (payload.periodEnd) {
    url = url + "&end=" + payload.periodEnd;
  }

  rest.doGet(url).then(
    (result) => {
      if (!result.hasDuplicatedKeys && result.numberOfPublished < result.numberOfProjects) {
        dispatch({
          [TYPE_KEY]: SET_NUMBER_OF_POSSIBLE_PROJECTS,
          [NUMBER_OF_POSSIBLE_PROJECTS]: result.numberOfProjects - result.numberOfPublished
        });

        dispatch({
          [TYPE_KEY]: SWITCH_PAGE
        });
      } else if (result.hasDuplicatedKeys) {
        dispatch(messagesReceived(createErrorMessages("template.activation.duplicate_key")));
      } else {
        dispatch(messagesReceived(createErrorMessages("template.activation.invalid_data")));
      }
    },
    () => {
      dispatch(messagesReceived(createErrorMessages("template.activation.invalid_request")));
    }
  );
}
